import { useState } from "react";
import { useAuth, Show } from "@clerk/react";
import { Navigate, useNavigate } from "react-router-dom";
import { makeApi } from "@/api";
import { useProfile } from "@/context/ProfileContext";
import { toast } from "sonner";
import { UploadSimple, MusicNotes, Image as ImageIcon, CheckCircle } from "@phosphor-icons/react";

const GENRES = ["Electronic", "House", "Techno", "Hip-Hop", "R&B", "Pop", "Indie", "Rock", "Ambient", "Synthwave", "Jazz", "Classical"];
const MOODS = ["Dark", "Driving", "Melancholic", "Euphoric", "Contemplative", "Nostalgic", "Aggressive", "Chill"];

const MAX_BYTES = 60 * 1024 * 1024;
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

// Resolves within 5s even if the browser can't read the file's metadata.
const readDuration = (file) => new Promise((resolve) => {
  const a = document.createElement("audio");
  const url = URL.createObjectURL(file);
  const done = (d) => { URL.revokeObjectURL(url); resolve(Number.isFinite(d) ? d : 0); };
  const timer = setTimeout(() => done(0), 5000);
  a.preload = "metadata";
  a.onloadedmetadata = () => { clearTimeout(timer); done(a.duration); };
  a.onerror = () => { clearTimeout(timer); done(0); };
  a.src = url;
});

function Form() {
  const { getToken } = useAuth();
  const api = makeApi(getToken);
  const navigate = useNavigate();
  const { refresh: refreshProfile } = useProfile();

  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("Electronic");
  const [mood, setMood] = useState("Dark");
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState("");
  const [pct, setPct] = useState(0);
  const [error, setError] = useState("");

  const upload = async (file, kind, label) => {
    setStage(label);
    setPct(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const r = await api.post("/upload", fd, {
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (ev) => ev.total && setPct(Math.round((ev.loaded / ev.total) * 100)),
    });
    return r.data.path;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !audioFile) {
      toast.error("Title and audio file are required");
      return;
    }
    if (audioFile.size > MAX_BYTES || (coverFile && coverFile.size > MAX_BYTES)) {
      setError("Files must be smaller than 60 MB.");
      return;
    }
    setUploading(true);
    try {
      setStage("Reading track");
      const duration = await readDuration(audioFile);
      const audio_path = await upload(audioFile, "audio", "Uploading audio");
      const cover_path = coverFile ? await upload(coverFile, "cover", "Uploading cover art") : null;
      setStage("Saving track");
      const r = await api.post("/tracks", { title: title.trim(), genre, mood, duration, audio_path, cover_path }, { timeout: 30000 });
      setStage("Saved");
      refreshProfile();
      toast.success(`"${r.data.title}" is saved and live`);
      setTimeout(() => navigate(`/artist/${r.data.artist_id}`), 1200);
    } catch (err) {
      const msg = err?.code === "ECONNABORTED"
        ? "The upload timed out. Check your connection and try again."
        : err?.response?.data?.detail || err?.message || "Upload failed";
      setError(msg);
      toast.error(msg);
      setUploading(false);
      setStage("");
    }
  };

  return (
    <form onSubmit={onSubmit} className="player-space px-4 md:px-10 py-12 max-w-5xl">
      <div className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-red-500 mb-3">Release / Distribution</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter uppercase leading-[0.9]">Ship a Track</h1>
      <p className="font-editorial text-2xl text-stone-300 mt-6 max-w-2xl">
        "The hardest part was always the gatekeeper. There is no gatekeeper here."
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
        <div className="hard-card p-8">
          <label className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-neutral-400">Track Title</label>
          <input
            data-testid="input-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="UNTITLED (001)"
            className="w-full bg-transparent border-b border-neutral-700 focus:border-red-600 outline-none py-3 font-display text-2xl font-bold mt-2 transition-colors"
          />

          <div className="grid grid-cols-2 gap-4 mt-8">
            <div>
              <label className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-neutral-400">Genre</label>
              <select data-testid="input-genre" value={genre} onChange={(e) => setGenre(e.target.value)}
                      className="w-full bg-neutral-900 border border-neutral-800 px-3 py-2.5 mt-2 font-mono-ui text-xs uppercase tracking-widest focus:border-red-600 outline-none">
                {GENRES.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-neutral-400">Mood</label>
              <select data-testid="input-mood" value={mood} onChange={(e) => setMood(e.target.value)}
                      className="w-full bg-neutral-900 border border-neutral-800 px-3 py-2.5 mt-2 font-mono-ui text-xs uppercase tracking-widest focus:border-red-600 outline-none">
                {MOODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <FileDrop
            testId="input-audio"
            icon={<MusicNotes size={28} />}
            label="Audio File"
            hint="MP3, WAV, FLAC • up to 60MB"
            accept="audio/*"
            file={audioFile}
            onFile={setAudioFile}
          />
          <FileDrop
            testId="input-cover"
            icon={<ImageIcon size={28} />}
            label="Cover Art (optional)"
            hint="JPG or PNG, square 1500x1500 recommended"
            accept="image/*"
            file={coverFile}
            onFile={setCoverFile}
          />
        </div>
      </div>

      {uploading && (
        <div className="mt-10 hard-card p-6 max-w-xl" data-testid="upload-progress">
          <div className="flex justify-between font-mono-ui text-[11px] uppercase tracking-[0.25em]">
            <span data-testid="upload-stage">{stage}</span>
            {stage.startsWith("Uploading") && <span data-testid="upload-percent">{pct}%</span>}
          </div>
          <div className="h-1 bg-neutral-800 mt-3">
            <div className="h-full bg-red-600 transition-[width] duration-300"
                 style={{ width: stage === "Saved" ? "100%" : stage.startsWith("Uploading") ? `${pct}%` : "15%" }} />
          </div>
        </div>
      )}
      {error && (
        <div className="mt-10 border border-red-700 bg-red-950/40 p-4 max-w-xl font-mono-ui text-xs text-red-200" data-testid="upload-error">
          {error}
        </div>
      )}

      <div className="mt-8 flex items-center gap-4">
        <button
          type="submit"
          disabled={uploading}
          data-testid="submit-release-btn"
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.3em] transition-colors"
        >
          <UploadSimple size={16} />
          {uploading ? (stage === "Saved" ? "Saved" : "Working...") : "Release Track"}
        </button>
        <span className="font-mono-ui text-[11px] text-neutral-500 uppercase tracking-widest">
          You keep 100% rights · Payouts weekly · Delivered to all major platforms*
        </span>
      </div>
    </form>
  );
}

function FileDrop({ testId, icon, label, hint, accept, file, onFile }) {
  return (
    <label className="hard-card p-6 flex items-center gap-5 cursor-pointer hover:border-neutral-600 transition-colors block" data-testid={testId}>
      <input type="file" accept={accept} className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
      <div className={`w-14 h-14 border border-neutral-800 flex items-center justify-center ${file ? "text-red-500" : "text-neutral-500"}`}>
        {file ? <CheckCircle size={28} weight="fill" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold">{label}</div>
        <div className="font-mono-ui text-[11px] uppercase tracking-widest text-neutral-500 mt-1">{hint}</div>
        {file && <div className="font-mono-ui text-[11px] text-neutral-300 mt-2 truncate">{file.name}</div>}
      </div>
    </label>
  );
}

export default function Upload() {
  return (
    <>
      <Show when="signed-in"><Form /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
